// composables/useAnalytics.ts
import { ApiClient, type ApiClientOptions } from "~~/shared/api/ApiClient";
import { useApiBaseUrl } from "~~/shared/models";
import { SocialInteractionService } from "~~/shared/services/socialInteraction";
import type { ContentType } from "~~/shared/models/social-interaction/request";
import {
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_OPTIONS,
  normalizeCookieConsent,
  type CookieConsentValue,
} from "~/utils/privacyConsent";
import {
  captureAnalyticsEvent,
  captureCompletedPurchase,
  type AnalyticsEvent,
} from "~/utils/productAnalytics";

export function useAnalytics() {
  const nuxtApp = useNuxtApp();
  const cookieConsent = useCookie<CookieConsentValue>(
    COOKIE_CONSENT_KEY,
    COOKIE_CONSENT_OPTIONS,
  );

  // Create service lazily on first tracking call so it is always within Nuxt context
  let _svc: SocialInteractionService | null = null;
  function getService(): SocialInteractionService {
    if (!_svc) {
      const opts: ApiClientOptions = { baseUrl: useApiBaseUrl() };
      _svc = new SocialInteractionService(new ApiClient(opts));
    }
    return _svc;
  }

  function hasAnalyticsConsent() {
    return normalizeCookieConsent(cookieConsent.value)?.analytics === true;
  }

  const emit = (name: AnalyticsEvent, props?: Record<string, unknown>) => {
    if (!import.meta.client) return false;
    return captureAnalyticsEvent(
      nuxtApp.$posthog?.(),
      hasAnalyticsConsent(),
      name,
      props,
    );
  };

  const purchaseCompleted = (
    transactionId: string,
    props: Record<string, unknown>,
  ) => {
    if (!import.meta.client) return false;
    let storage: Storage | undefined;
    try {
      storage = localStorage;
    } catch {
      /* Storage can be blocked. */
    }
    return captureCompletedPurchase(
      transactionId,
      (properties) => emit("purchase_completed", properties),
      props,
      storage,
    );
  };

  /**
   * Track that a detail page was opened (event or establishment).
   * Call once on mount of the detail page.
   */
  const trackOpened = (contentType: ContentType, elementID: string) => {
    if (!import.meta.client || !elementID || !hasAnalyticsConsent()) return;
    const svc = getService();
    svc.openedContent({ contentType, element_id: elementID }).catch((err) => {
      console.warn("[analytics] openedContent failed", err);
    });
  };

  /**
   * Track that a card became visible in the viewport (impression).
   * Typically called from an IntersectionObserver callback.
   */
  const trackViewed = (contentType: ContentType, elementID: string) => {
    if (!import.meta.client || !elementID || !hasAnalyticsConsent()) return;
    const svc = getService();
    svc.viewedContent({ contentType, element_id: elementID }).catch((err) => {
      console.warn("[analytics] viewedContent failed", err);
    });
  };

  /**
   * Track that a user clicked / tapped a card.
   */
  const trackClicked = (contentType: ContentType, elementID: string) => {
    if (!import.meta.client || !elementID || !hasAnalyticsConsent()) return;
    const svc = getService();
    svc.clickedContent({ contentType, element_id: elementID }).catch((err) => {
      console.warn("[analytics] clickedContent failed", err);
    });
  };

  return { emit, purchaseCompleted, trackOpened, trackViewed, trackClicked };
}
